import { useState } from "react";
import { AddressShape, ZoneDraft, ZoneShapeMode } from "../models/ZoneDraft";
import { circlePoints, closePolygon, LngLat, openRing, squarePoints } from "../utils/zoneGeometry";
import { suggestZoneName } from "../utils/zoneList";

export const DEFAULT_ZONE_SIZE_M = 100;
const MIN_VERTICES = 3;

interface EditorState {
  draft: ZoneDraft;
  mode: ZoneShapeMode;
  shape: AddressShape;
  sizeM: number;
  addressCenter: LngLat | null;
  drawing: boolean;
  /** The user typed a name: stop proposing one from the address. */
  nameTouched: boolean;
}

function shapePoints(center: LngLat | null, shape: AddressShape, sizeM: number): LngLat[] {
  if (!center) return [];
  return openRing(shape === "circle" ? circlePoints(center, sizeM) : squarePoints(center, sizeM));
}

/**
 * The form state of the zone being created or edited; `null` while the page shows the list.
 * In address mode the points follow address, shape and size. In draw mode they are clicked in.
 */
export function useZoneEditor() {
  const [state, setState] = useState<EditorState | null>(null);

  const update = (change: (s: EditorState) => EditorState) => setState(s => (s ? change(s) : s));
  const withAddressPoints = (s: EditorState): EditorState =>
    s.mode === "address"
      ? { ...s, draft: { ...s.draft, points: shapePoints(s.addressCenter, s.shape, s.sizeM) } }
      : s;

  return {
    draft: state?.draft ?? null,
    mode: state?.mode ?? "address",
    shape: state?.shape ?? "circle",
    sizeM: state?.sizeM ?? DEFAULT_ZONE_SIZE_M,
    addressCenter: state?.addressCenter ?? null,
    drawing: state?.drawing ?? false,

    start(draft: ZoneDraft, mode: ZoneShapeMode) {
      setState({
        draft,
        mode,
        shape: "circle",
        sizeM: DEFAULT_ZONE_SIZE_M,
        addressCenter: null,
        // An existing zone opens with its points ready to drag, a new drawing starts clicking.
        drawing: mode === "draw" && draft.points.length === 0,
        nameTouched: draft.name !== "",
      });
    },
    stop() {
      setState(null);
    },
    setField<K extends keyof ZoneDraft>(key: K, value: ZoneDraft[K]) {
      update(s => ({ ...s, draft: { ...s.draft, [key]: value }, nameTouched: s.nameTouched || key === "name" }));
    },
    setMode(mode: ZoneShapeMode) {
      update(s =>
        mode === "draw"
          ? { ...s, mode, drawing: s.draft.points.length === 0 }
          : withAddressPoints({ ...s, mode, drawing: false }),
      );
    },
    setAddress(center: LngLat, label: string) {
      update(s =>
        withAddressPoints({
          ...s,
          addressCenter: center,
          draft: s.nameTouched ? s.draft : { ...s.draft, name: suggestZoneName(label) },
        }),
      );
    },
    setShape(shape: AddressShape) {
      update(s => withAddressPoints({ ...s, shape }));
    },
    setSizeM(sizeM: number) {
      update(s => withAddressPoints({ ...s, sizeM }));
    },
    addPoint(point: LngLat) {
      update(s => (s.drawing ? { ...s, draft: { ...s.draft, points: [...s.draft.points, point] } } : s));
    },
    undoPoint() {
      update(s => ({ ...s, drawing: true, draft: { ...s.draft, points: s.draft.points.slice(0, -1) } }));
    },
    resetPoints() {
      update(s => ({ ...s, drawing: true, draft: { ...s.draft, points: [] } }));
    },
    /** `toleranceM`: points closer than this to their predecessor are dropped (double-click jitter). */
    finishDrawing(toleranceM = 0) {
      update(s => {
        const points = openRing(closePolygon(s.draft.points, toleranceM));
        return points.length < MIN_VERTICES ? s : { ...s, drawing: false, draft: { ...s.draft, points } };
      });
    },
    moveVertex(index: number, point: LngLat) {
      update(s => ({
        ...s,
        draft: { ...s.draft, points: s.draft.points.map((p, i) => (i === index ? point : p)) },
      }));
    },
    removeVertex(index: number) {
      update(s =>
        s.draft.points.length <= MIN_VERTICES
          ? s
          : { ...s, draft: { ...s.draft, points: s.draft.points.filter((_, i) => i !== index) } },
      );
    },
  };
}

export type ZoneEditor = ReturnType<typeof useZoneEditor>;
